import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "../auth";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ArrowLeft, Trash2, User, MapPin, Calendar, Tag, Image as ImageIcon, X, Pencil } from "lucide-react";
import { parseLocalDate } from "../utils/date";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function ReportDetail() {
  const { id } = useParams();
  const { authHeaders, token, user } = useAuth();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reports/${id}`, { headers: authHeaders() });
        setR(data);
      } catch (e) {
        toast.error("Reporte no encontrado");
        navigate("/reports");
      }
      setLoading(false);
    })();
  }, [id]);

  const del = async () => {
    try {
      await axios.delete(`${API}/reports/${id}`, { headers: authHeaders() });
      toast.success("Reporte eliminado");
      navigate("/reports");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  if (loading) {
    return <div className="p-10 kbd-label text-slate-400">Cargando…</div>;
  }
  if (!r) return null;

  const photoUrl = (path) => `${API}/files/${path}`;

  const yn = (v) => v === true ? "Sí" : v === false ? "No" : null;
  const fields = [
    { label: "Disponibilidad de Producto", v: yn(r.availability), kind: "badge" },
    { label: "Visibilidad", v: r.visibility ? `${r.visibility} / 5` : null, kind: "visibility" },
    { label: "Precio al Consumidor (Marca; Empaque; Precio)", v: r.price_info, kind: "text", span: true },
    { label: "Exhibición o Espacio Adicional", v: yn(r.additional_display), kind: "badge" },
    { label: "¿La competencia tiene promoción?", v: yn(r.competitor_promo), kind: "badge" },
    { label: "Describa la promoción de la competencia", v: r.competitor_promo_desc, kind: "text", span: true },
    { label: "¿Productos próximos a vencer o vencidos?", v: yn(r.expiring_products), kind: "badge" },
    { label: "Tarea o recomendación al mercaderista", v: r.merchandiser_task, kind: "text", span: true },
    { label: "Comentarios", v: r.comments, kind: "text", span: true },
  ].filter((f) => f.v !== null && f.v !== "");

  return (
    <div className="p-5 md:p-8 lg:p-12 max-w-5xl mx-auto" data-testid="report-detail-page">
      <button onClick={() => navigate(-1)} className="kbd-label flex items-center gap-2 mb-6 text-slate-500 hover:text-slate-900" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div className="min-w-0">
          <div className="kbd-label mb-2">/ Reporte de visita · {r.visit_type}</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-slate-950">
            {r.brand}
          </h1>
          <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-600">
            <InfoChip icon={Calendar} label={parseLocalDate(r.fecha).toLocaleDateString("es", { day: "numeric", month: "long", year: "numeric" })} />
            <InfoChip icon={Tag} label={r.chain} />
            <InfoChip icon={MapPin} label={`${r.branch}, ${r.city || "—"}`} />
            <InfoChip icon={User} label={`${r.visitor_name}${r.visitor_position ? ` · ${r.visitor_position}` : ""}`} />
          </div>
        </div>

        {(user?.role === "admin" || user?.id === r.user_id) && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/reports/${id}/edit`)}
              className="gap-2 border-emerald-200 text-emerald-800 hover:bg-emerald-50"
              data-testid="edit-report-btn"
            >
              <Pencil className="w-4 h-4" /> Editar
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="gap-2 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" data-testid="delete-report-btn">
                  <Trash2 className="w-4 h-4" /> Eliminar
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar reporte?</AlertDialogTitle>
                  <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-delete">Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">
                    Eliminar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      {r.photos?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 md:p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon className="w-4 h-4 text-slate-400" />
            <div className="kbd-label">/ Fotografías · {r.photos.length}</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {r.photos.map((path, i) => (
              <button
                key={path}
                onClick={() => setLightbox(path)}
                className="aspect-square bg-slate-100 rounded-md overflow-hidden hover:ring-2 hover:ring-emerald-600"
                data-testid={`photo-${i}`}
              >
                <img src={photoUrl(path)} alt={`foto ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.label} className={`bg-white border border-slate-200 rounded-lg p-5 tv-ticker-line pl-6 ${f.span ? "md:col-span-2" : ""}`}>
            <div className="kbd-label">{f.label}</div>
            {f.kind === "badge" ? (
              <div className={`inline-flex items-center mt-2 px-3 py-1.5 rounded-md text-sm font-semibold ${f.v === "Sí" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>
                {f.v}
              </div>
            ) : f.kind === "visibility" ? (
              <div className="mt-2 flex items-center gap-3">
                <div className="flex gap-1">
                  {[1,2,3,4,5].map((n) => (
                    <div key={n} className={`w-7 h-7 rounded-full font-display font-bold text-xs flex items-center justify-center ${n <= r.visibility ? "bg-emerald-700 text-white" : "bg-slate-100 text-slate-400"}`}>{n}</div>
                  ))}
                </div>
                <span className="text-sm text-slate-600 font-medium">{f.v}</span>
              </div>
            ) : (
              <p className="text-slate-900 mt-2 whitespace-pre-wrap leading-relaxed">{f.v}</p>
            )}
          </div>
        ))}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-slate-950/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
          <img src={photoUrl(lightbox)} alt="full" className="max-h-[90vh] max-w-[90vw] object-contain" />
        </div>
      )}
    </div>
  );
}

function InfoChip({ icon: Icon, label }) {
  return (
    <div className="inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 rounded-md">
      <Icon className="w-3.5 h-3.5 text-slate-500" />
      <span className="text-xs font-medium text-slate-800">{label}</span>
    </div>
  );
}
